import { useState, useCallback } from 'react';
import { ResolvedMatch, MeetingFormData, Employee, AttendeePriority, RepeatOption } from '../interfaces/interface';
import { DocumentService } from '../services/DocumentService';
import { nameResolver } from '../utils/NameResolver';
import { EmployeeService } from '../services/EmployeeService';
import { logger, handleError } from '../../../../logger';

/** Default user-friendly error message for non-backend runtime errors */
const DEFAULT_DOCUMENT_ERROR =
  'Something went wrong while processing your document. Please ensure your file follows the expected ' +
  'meeting request template format and try again. If the issue persists, please create a support ticket.';

/**
 * Extracts the error message — shows backend error directly to the user.
 * Falls back to a generic message only if no message is available.
 */
function getUserFriendlyError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return DEFAULT_DOCUMENT_ERROR;
}

import { MEETING_CONSTANTS } from '../constants/EAHuddleConstants';

/**
 * Batching mechanism to limit concurrent API calls
 * @param calls Array of promise-returning functions
 * @param batchSize Number of concurrent calls per batch
 * @returns Promise that resolves to array of all results
 */
async function batchApiCalls<T>(calls: (() => Promise<T>)[], batchSize = MEETING_CONSTANTS.ATTENDEE_BATCH_SIZE): Promise<T[]> {
  const results: T[] = [];
  for (let i = 0; i < calls.length; i += batchSize) {
    const batch = calls.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map((call) => call()));
    results.push(...batchResults);
    if (i + batchSize < calls.length) {
      await new Promise((resolve) => setTimeout(resolve, MEETING_CONSTANTS.BATCH_DELAY_MS));
    }
  }
  return results;
}

const lookupExecutiveAssistant = async (selectedEmployee: any, attendeeType: string): Promise<any | null> => {
  let selectedExecutiveAssistant = null;
  if (selectedEmployee) {
    try {
      selectedExecutiveAssistant = await EmployeeService.findExecutiveAssistant(selectedEmployee);

      if (selectedExecutiveAssistant) {
        logger.info(`Executive assistant found for ${attendeeType} attendee`, {
          hasEmployee: true,
          hasEA: true
        });
      }
    } catch (error) {
      logger.warn(`Failed to lookup executive assistant for ${attendeeType} attendee`, {
        hasEmployee: true,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }
  return selectedExecutiveAssistant;
};

export const useDocumentProcessing = () => {
  const [isProcessingDocument, setIsProcessingDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string>('');
  const [documentDescription, setDocumentDescription] = useState<string>('');
  const [docxPreviewHTML, setDocxPreviewHTML] = useState<string>('');
  const [htmlTemplate, setHtmlTemplate] = useState<string>('');
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  const handleFormattedContentExtracted = useCallback((htmlContent: string) => {
    setDocxPreviewHTML(htmlContent);
    if (htmlContent && htmlContent.trim().length > 0) {
      setIsPreviewVisible(true);
    }
  }, []);

  const processDocument = useCallback(
    async (
      file: File,
      eventsApiContext: any,
      userAlias: string,
      manuallyEditedFields: Set<keyof MeetingFormData>
    ): Promise<{
      success: boolean;
      parsedAttendees?: ResolvedMatch[];
      formUpdates?: Partial<MeetingFormData>;
      htmlContent?: string;
      unresolvedOnBehalfOf?: string;
    }> => {
      setIsPreviewVisible(false);
      setDocxPreviewHTML('');

      setIsProcessingDocument(true);
      setDocumentError('');

      try {
        const parseResult = await DocumentService.parseDocument(
          file,
          eventsApiContext,
          userAlias,
          'Parse this EA Meeting Request Template and extract the meeting details.'
        );

        if (!parseResult.success) {
          setDocumentError(getUserFriendlyError(new Error(parseResult.error || 'Failed to parse document')));
          return { success: false };
        }

        if (!parseResult.data) {
          setDocumentError('No meeting data found in document');
          return { success: false };
        }

        const parsedMeeting = parseResult.data;

        if (parseResult.htmlContent) {
          setHtmlTemplate(parseResult.htmlContent);
        }

        // Combine all attendee names for batch processing
        const allAttendeeNames = [
          ...(parsedMeeting.CriticalAttendees || []),
          ...parsedMeeting.RequiredAttendees,
          ...parsedMeeting.OptionalAttendees
        ].filter((name) => name && name.trim().length > 0);

        if (allAttendeeNames.length > MEETING_CONSTANTS.MAX_DOCUMENT_ATTENDEES) {
          setDocumentError(
            `The document contains ${allAttendeeNames.length} attendees, which exceeds the limit of ${MEETING_CONSTANTS.MAX_DOCUMENT_ATTENDEES}. Please reduce the number of attendees and try again.`
          );
          return { success: false };
        }

        logger.info('Starting batch employee data fetch', {
          totalNames: allAttendeeNames.length
        });

        // Batch fetch employee data for all names in groups of 5
        // Use Map to track which employees belong to which name
        const employeeDataByName = new Map<string, Employee[]>();
        if (allAttendeeNames.length > 0) {
          try {
            // Create batched API calls with smart lookup: try alias first, fall back to name search
            const searchCalls = allAttendeeNames.map((name) => async () => {
              // Try alias lookup first (uses DynamoDB cache → PAPI fallback)
              const aliasResult = await EmployeeService.getEmployeeByLogin(name);

              if (aliasResult) {
                // Found via alias - return single employee as array
                logger.info('Employee found via alias lookup', { name });
                return { name, employees: [aliasResult] };
              }

              // No alias match - fall back to name search
              logger.info('Alias lookup failed, falling back to name search', { name });
              const searchResults = await EmployeeService.searchEmployees(name, 15);
              return { name, employees: searchResults };
            });

            const batchResults = await batchApiCalls(searchCalls);

            // Store results in Map keyed by name
            batchResults.forEach((result) => {
              employeeDataByName.set(result.name, result.employees);
            });

            logger.info('Employee data fetched successfully', {
              totalNames: employeeDataByName.size
            });
          } catch (error) {
            handleError('Failed to fetch employee data', error);
            setDocumentError('We had trouble looking up some attendee names from the document. Please try again or add the attendees manually.');
            return { success: false };
          }
        }

        // Process attendees using fetched employee data
        const parsedAttendees: ResolvedMatch[] = [];
        let nextId = 1;

        const processAttendeeGroup = async (names: string[], priority: AttendeePriority) => {
          for (const name of names) {
            const nameSpecificEmployees = employeeDataByName.get(name) || [];
            const matches = nameResolver.findMatchesWithEmployeeData(name, nameSpecificEmployees);
            const selectedEmployee = matches.selectedEmployee;
            const selectedExecutiveAssistant = await lookupExecutiveAssistant(selectedEmployee, priority);

            parsedAttendees.push({
              id: `doc-${nextId++}`,
              inputName: name,
              matches: matches.matches,
              selectedEmployee,
              selectedExecutiveAssistant,
              status: matches.status,
              priority,
              resolvedEmail: selectedEmployee?.employee_internal_email_address || ''
            });
          }
        };

        await processAttendeeGroup(parsedMeeting.CriticalAttendees || [], AttendeePriority.Critical);
        await processAttendeeGroup(parsedMeeting.RequiredAttendees, AttendeePriority.Required);
        await processAttendeeGroup(parsedMeeting.OptionalAttendees, AttendeePriority.Optional);

        const formUpdates: Partial<MeetingFormData> = {};
        let unresolvedOnBehalfOf: string | undefined;
        if (parsedMeeting.Subject && !manuallyEditedFields.has('subject')) {
          formUpdates.subject = parsedMeeting.Subject;
        }
        if (parsedMeeting.Duration && !manuallyEditedFields.has('duration')) {
          const match = parsedMeeting.Duration.match(/(\d+)/);
          if (match) {
            const parsed = parseInt(match[1], 10);
            if (parsed > 0) formUpdates.duration = parsed;
          }
        }
        if (parsedMeeting.Agenda && !manuallyEditedFields.has('agenda')) {
          formUpdates.agenda = parsedMeeting.Agenda;
        }
        if (parsedMeeting.Recurrence && !manuallyEditedFields.has('repeat')) {
          const validOptions: readonly RepeatOption[] = ['daily', 'weekly', 'monthly', 'none'] as const;
          const normalized = parsedMeeting.Recurrence.toLowerCase() as RepeatOption;
          if (validOptions.includes(normalized)) formUpdates.repeat = normalized;
        }
        if (parsedMeeting.OnBehalfOf && !manuallyEditedFields.has('onBehalfOfLogin')) {
          const onBehalfOfName = parsedMeeting.OnBehalfOf.trim();
          if (onBehalfOfName) {
            try {
              const aliasResult = await EmployeeService.getEmployeeByLogin(onBehalfOfName);
              if (aliasResult) {
                formUpdates.onBehalfOfLogin = aliasResult.login_name;
              } else {
                const searchResults = await EmployeeService.searchEmployees(onBehalfOfName, 5);
                const bestMatch = nameResolver.findMatchesWithEmployeeData(onBehalfOfName, searchResults);
                if (bestMatch.selectedEmployee) {
                  formUpdates.onBehalfOfLogin = bestMatch.selectedEmployee.login_name;
                } else {
                  unresolvedOnBehalfOf = onBehalfOfName;
                }
              }
            } catch (error) {
              logger.warn('Failed to resolve OnBehalfOf from document', { onBehalfOfName, error });
              unresolvedOnBehalfOf = onBehalfOfName;
            }
          }
        }

        logger.info('Document processed successfully', {
          formUpdates,
          unresolvedOnBehalfOf,
          hasDescription: !!parseResult.htmlContent
        });

        return {
          success: true,
          parsedAttendees,
          formUpdates,
          htmlContent: parseResult.htmlContent,
          unresolvedOnBehalfOf
        };
      } catch (error) {
        handleError('Document processing failed', error);
        setDocumentError(getUserFriendlyError(error));
        return { success: false };
      } finally {
        setIsProcessingDocument(false);
      }
    },
    []
  );

  const resetDocumentState = useCallback(() => {
    setIsProcessingDocument(false);
    setDocumentError('');
    setDocumentDescription('');
    setDocxPreviewHTML('');
    setHtmlTemplate('');
    setIsPreviewVisible(false);
  }, []);

  return {
    isProcessingDocument,
    documentError,
    documentDescription,
    docxPreviewHTML,
    htmlTemplate,
    isPreviewVisible,
    handleFormattedContentExtracted,
    processDocument,
    resetDocumentState,
    setDocumentDescription,
    setIsPreviewVisible,
    setDocumentError
  };
};
